/**
 * Records (WALLIYO-0131): the HR side of the employee list.
 *
 * The directory answers "who do I ask about this" and is open to everybody.
 * This one answers "what is true of this employee" — when they joined, when
 * they were last here, and whether something granted to them by name has been
 * sitting there with no end date. Same people, different question, and the
 * second question needs a feature.
 *
 * The narrowing lives here rather than in the component so it can be tested
 * without a browser, and so the three filters compose in one place instead of
 * three nested conditionals in a render.
 */

import type { StaffMemberListItem } from "@wallyo/contracts/console";

export type RecordsFilters = {
  /** A name, an email, or an exact UUID-like number. Empty matches everybody. */
  query: string;
  /** A team name, or "" for any. */
  team: string;
  /** A role name, or "" for any. */
  role: string;
};

export const NO_RECORDS_FILTERS: RecordsFilters = { query: "", team: "", role: "" };

const matches = (haystack: string, needle: string) => haystack.toLowerCase().includes(needle);

/**
 * Whether somebody matches the typed term.
 *
 * Name and email, because those are the two things somebody knows when they
 * are looking for a colleague. Not the role or the team: those have their own
 * filters, and folding them in would make "Support" match both a person's
 * team and a person's name with no way to tell which was meant.
 */
function matchesQuery(member: StaffMemberListItem, query: string): boolean {
  if (query === "") return true;
  const needle = query.toLowerCase();
  return matches(member.name, needle) || matches(member.email, needle);
}

export function narrow(staff: readonly StaffMemberListItem[], filters: RecordsFilters): readonly StaffMemberListItem[] {
  const query = filters.query.trim();
  return staff.filter(
    (member) =>
      matchesQuery(member, query) &&
      (filters.team === "" || member.teams.some((team) => team.name === filters.team)) &&
      (filters.role === "" || member.roles.some((role) => role.name === filters.role)),
  );
}

/** Every team anybody is on, for the filter. Sorted, and each named once. */
export function teamNames(staff: readonly StaffMemberListItem[]): readonly string[] {
  const names = new Set<string>();
  for (const member of staff) for (const team of member.teams) names.add(team.name);
  return [...names].sort((left, right) => left.localeCompare(right));
}

/** Every role anybody holds, for the filter. */
export function roleNames(staff: readonly StaffMemberListItem[]): readonly string[] {
  const names = new Set<string>();
  for (const member of staff) for (const role of member.roles) names.add(role.name);
  return [...names].sort((left, right) => left.localeCompare(right));
}

/**
 * How many people hold something granted by name with no end date.
 *
 * Counted over everybody rather than over the narrowed list: it is a figure
 * about the company, and one that changed because a filter was typed would be
 * read as somebody having fixed something.
 */
export function dueAReview(staff: readonly StaffMemberListItem[]): number {
  return staff.filter((member) => member.openGrants > 0).length;
}
